import React, { HTMLAttributes, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { setPollExpanded } from '../../actions/configurationActions';

import * as QPlayerSVGElement from '../QPlayerSVGElement';
import { PollMessage, PollPanelHeader, PollPanelHeaderTitle, PollPanelWrapper, PollQuestion, PollQuestionTitle } from '../Poll/Poll.styled';

interface QuestionPanelProps extends React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	t: any;
	settings?: any;
	qna?: any;
	noShadow?: boolean;
	onSendQuestionClick?: (_event: React.MouseEvent | React.KeyboardEvent, _question: any) => void;
	onFocusOnQuestionField?: () => void;
}

const QuestionPanelInput = styled.textarea`
	text-align: left;
	font-weight: normal;
	font-size: 0.6875rem;
	font-family: inherit;
	width: 100%;
	box-sizing: border-box;
	padding: 10px;
	border: 1px solid #c5ced3;
	border-radius: 4px;
	overflow: auto;
	background-color: #ffffff;

	:empty:before {
		content: attr(placeholder);
		cursor: text;
	}
`;

const QuestionPanelAction = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	margin-top: 6px;
`;

const QuestionPanelActionButton = styled.button<{ primaryColor?: string }>`
	height: 32px;
	border: none;
	background-color: ${(props) => props.primaryColor};
	border-radius: 4px;
	box-shadow: 0px 3px 3px #0000005c;
	color: #ffffff;
	font-family: inherit;
	font-weight: normal;
	font-size: 0.75rem;
	padding: 0px 10px;

	&:disabled {
		opacity: 0.4;
		box-shadow: none;
	}
`;

const QuestionPanel: React.FC<QuestionPanelProps> = ({ t, qna, settings, noShadow, onSendQuestionClick, onFocusOnQuestionField }) => {
	const dispatch = useDispatch();

	const { isChatActivated } = useSelector((state) => (state as any).configurationReducer);

	const [isExpanded, setExpanded] = useState<boolean>(true);
	const [isQuestionSent, setIsQuestionSent] = useState(false);
	const [questionText, setQuestionText] = useState('');

	const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';

	const onSendButtonClick = useCallback(
		(e) => {
			onSendQuestionClick?.(e, {
				text: questionText,
			});

			setQuestionText('');
			setIsQuestionSent(true);
			setExpanded(false);
		},
		[onSendQuestionClick, questionText]
	);

	const onQuestionTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setQuestionText(event.target.value);
	}, []);

	useEffect(() => {
		if (!isQuestionSent) {
			return;
		}
		setTimeout(() => setIsQuestionSent(false), 3000);
	}, [isQuestionSent]);

	useEffect(() => {
		dispatch(setPollExpanded(isExpanded));
	}, [isExpanded]);

	return qna ? (
		<PollPanelWrapper className="qna__wrapper" isChatActivated={isChatActivated} noShadow={noShadow}>
			<PollPanelHeader onClick={() => setExpanded((prev) => !prev)} primaryColor={primaryColor === '#126cfc' ? undefined : primaryColor}>
				<PollPanelHeaderTitle>
					<QPlayerSVGElement.Question color={primaryColor === '#126cfc' ? undefined : primaryColor} />
					Q&A
				</PollPanelHeaderTitle>
				{isExpanded ? <QPlayerSVGElement.DownIcon /> : <QPlayerSVGElement.UpIcon />}
			</PollPanelHeader>

			{isQuestionSent && <PollMessage>{t('QUESTION_MESSAGE')}</PollMessage>}

			{!isQuestionSent && (
				<PollQuestion>
					<PollQuestionTitle isExpanded={isExpanded}>{qna.text}</PollQuestionTitle>
				</PollQuestion>
			)}

			{!isQuestionSent && isExpanded && (
				<>
					<QuestionPanelInput
						placeholder={t('QUESTION_PLACEHOLDER')}
						value={questionText}
						onChange={onQuestionTextChange}
						onFocus={onFocusOnQuestionField}
					/>

					<QuestionPanelAction>
						<QuestionPanelActionButton
							disabled={!questionText}
							onClick={onSendButtonClick}
							primaryColor={primaryColor === '#126cfc' ? undefined : primaryColor}
						>
							{t('LABEL_QUESTION_ACTION')}
						</QuestionPanelActionButton>
					</QuestionPanelAction>
				</>
			)}
		</PollPanelWrapper>
	) : null;
};

export default QuestionPanel;
